import { motion, useReducedMotion } from "framer-motion";
import { Check, Navigation, Circle, ChevronRight, Flag } from "lucide-react";

/**
 * RiverMap.
 *
 * The immersive vertical "water flow" for a goal. A central teal stream
 * runs top to bottom, filled to the person's progress, with a droplet
 * that drifts down it. Each phase is a stop: alternating cards on
 * desktop, a left rail on mobile. Clicking a stop opens its drawer.
 */
function stateOf(phase, index, currentIndex) {
  if (phase.status === "done") return "done";
  if (index === currentIndex) return "current";
  if (index < currentIndex) return "done";
  return "locked";
}

const NODE = {
  done: { bg: "hsl(var(--klareet-teal))", fg: "#fff", ring: "transparent", Icon: Check },
  current: { bg: "hsl(var(--klareet-canvas))", fg: "hsl(var(--klareet-teal))", ring: "hsl(var(--klareet-teal))", Icon: Navigation },
  locked: { bg: "hsl(var(--klareet-canvas-2))", fg: "hsl(var(--klareet-muted))", ring: "transparent", Icon: Circle },
};

export function RiverMap({ phases, currentIndex, onOpen }) {
  const reduce = useReducedMotion();
  const total = phases.length || 1;
  const doneCount = phases.filter((p) => p.status === "done").length;
  const fillPct = Math.round((doneCount / total) * 100);

  return (
    <div className="relative" data-testid="river-map">
      {/* central stream */}
      <div className="absolute top-2 bottom-2 w-1.5 rounded-full left-6 md:left-1/2 md:-translate-x-1/2" style={{ background: "hsl(var(--klareet-line-soft))" }}>
        <motion.div
          className="absolute top-0 left-0 right-0 rounded-full overflow-hidden"
          initial={{ height: 0 }}
          animate={{ height: `${fillPct}%` }}
          transition={reduce ? { duration: 0 } : { duration: 1.1, ease: "easeInOut" }}
          style={{ background: "linear-gradient(180deg, hsl(var(--klareet-teal)), hsl(160 55% 42%))" }}
        >
          {!reduce && fillPct > 0 && (
            <motion.span
              className="absolute left-1/2 -translate-x-1/2 w-2 h-2 rounded-full"
              style={{ background: "#fff", boxShadow: "0 0 8px hsl(var(--klareet-teal))" }}
              animate={{ top: ["0%", "100%"] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeIn" }}
            />
          )}
        </motion.div>
      </div>

      <div className="space-y-6 md:space-y-2">
        {phases.map((p, i) => {
          const st = stateOf(p, i, currentIndex);
          const cfg = NODE[st];
          const Icon = cfg.Icon;
          const rightSide = i % 2 === 1;
          const doneSubs = (p.subtasks || []).filter((t) => t.done).length;
          const totalSubs = (p.subtasks || []).length;

          const card = (
            <button
              type="button"
              onClick={() => onOpen(p, i)}
              data-testid={`river-node-${i}`}
              className={`group w-full text-left rounded-2xl p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-md ${rightSide ? "md:text-left" : "md:text-right"}`}
              style={{
                background: st === "current" ? "hsl(var(--klareet-teal) / 0.06)" : "hsl(var(--klareet-canvas))",
                border: `1px solid ${st === "current" ? "hsl(var(--klareet-teal) / 0.35)" : "hsl(var(--klareet-line-soft))"}`,
              }}
            >
              <div className={`flex items-center gap-2 ${rightSide ? "" : "md:flex-row-reverse"}`}>
                <span className="klareet-mono !text-[10px]" style={{ color: "hsl(var(--klareet-muted))" }}>{p.window}</span>
                {st === "done" && <span className="k-chip k-chip-teal !text-[10px]"><Check size={10} /> done</span>}
                {st === "current" && <span className="k-chip !text-[10px]" style={{ color: "hsl(var(--klareet-teal))" }}><Flag size={10} /> you are here</span>}
              </div>
              <div className="klareet-serif mt-1" style={{ fontSize: "1.15rem", lineHeight: 1.3, color: "hsl(var(--klareet-ink))" }}>{p.title}</div>
              <div className={`k-quiet !text-xs mt-2 flex items-center gap-1.5 ${rightSide ? "" : "md:justify-end"}`}>
                {totalSubs > 0 && <span>{doneSubs} of {totalSubs} steps</span>}
                {p.reminder_on && <span className="klareet-mono">· due {p.reminder_on}</span>}
                <ChevronRight size={13} className="opacity-40 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          );

          return (
            <motion.div
              key={p.id}
              initial={reduce ? false : { opacity: 0, y: 12 }}
              whileInView={reduce ? {} : { opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: Math.min(i * 0.06, 0.4) }}
              className="relative pl-16 md:pl-0 md:grid md:grid-cols-2 md:gap-10 md:items-center"
            >
              {/* node marker */}
              <span
                className="absolute z-10 top-4 md:top-1/2 left-6 md:left-1/2 -translate-x-1/2 md:-translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center"
                style={{ background: cfg.bg, color: cfg.fg, border: `2px solid ${st === "locked" ? "hsl(var(--klareet-line))" : "hsl(var(--klareet-teal))"}` }}
                data-testid={`river-node-marker-${i}`}
              >
                {st === "current" && !reduce && (
                  <span className="absolute inset-0 rounded-full animate-ping" style={{ background: "hsl(var(--klareet-teal) / 0.35)" }} />
                )}
                <Icon size={16} className="relative" />
              </span>

              {/* card, alternating side on desktop */}
              {rightSide ? (
                <>
                  <div className="hidden md:block" />
                  <div className="md:col-start-2">{card}</div>
                </>
              ) : (
                <>
                  <div>{card}</div>
                  <div className="hidden md:block" />
                </>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

export default RiverMap;
